<template>
    <StepShell :embedded="embedded" layout="page" width="narrow" gap="none">
        <StepHeader
            v-if="embedded"
            embedded
            class="mb-6"
            :title="showFailure ? headlineCopy : t('onboarding.calendarFirst.sharpenedReadingsStep.title')"
            :lede="showFailure ? undefined : lede"
            source="perspective">
            <template v-if="showFailure" #note>
                <i aria-hidden="true" class="bi bi-exclamation-circle"></i>
                {{ eyebrowCopy }}
            </template>
        </StepHeader>
        <BasePageHeader v-else :title="headlineCopy" :subtitle="showFailure ? undefined : lede" no-border subtitle-below>
            <template v-if="showFailure" #eyebrow>
                <p class="eyebrow">
                    <i aria-hidden="true" class="bi bi-exclamation-circle"></i>
                    {{ eyebrowCopy }}
                </p>
            </template>
        </BasePageHeader>

        <!-- PerspectiveResultStep already presents the archetype; keep this screen focused on revised
             readings rather than repeating the personality result.
             -->

        <!-- The second pass saves readings independently as parallel jobs finish, so show each result
             immediately.
             -->
        <ReadingList v-if="cards.length > 0 || pendingSlots > 0" class="readings" :readings="cards" :pending-count="pendingSlots" :show-rating="showRating" :location="location" />

        <BaseNotification v-else theme="warning" hide-dismiss class="notice">
            <template #content>
                <div class="notice-body">
                    <p class="notice-head">{{ noticeHeadline }}</p>
                    <p class="notice-copy">{{ noticeCopy }}</p>
                    <p class="notice-reassurance">{{ t("onboarding.calendarFirst.sharpenedReadingsStep.notice.reassurance") }}</p>
                </div>
            </template>
        </BaseNotification>

        <!-- The linear flow explains the profile here; coaching setup has a
             separate introduction before entering the profile screen. -->
        <section v-if="!embedded" class="handoff">
            <p class="handoff-eyebrow">{{ t("onboarding.calendarFirst.sharpenedReadingsStep.handoff.eyebrow") }}</p>
            <h2 class="handoff-title">{{ t("onboarding.calendarFirst.sharpenedReadingsStep.handoff.title") }}</h2>
            <p class="handoff-body">{{ handoffCopy }}</p>
        </section>

        <!-- Keep profile skipping on the profile screen; this screen only presents results. -->
        <StepActions>
            <BaseButton theme="primary" @click="emit('continue')">{{
                embedded ? t("onboarding.calendarFirst.actions.continue") : t("onboarding.calendarFirst.sharpenedReadingsStep.viewProfile")
            }}</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import ReadingList from "~vue/CalendarFirstOnboarding/ReadingList.vue";
import StepHeader from "~vue/CalendarFirstOnboarding/StepHeader.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import type { LabInsightLocation } from "~vue/types/labs";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";
import type { AccessLevel, ReadingsState } from "./types";

const {
    embedded = false,
    readings,
    settled,
    availableAccessLevels,
    calendarConnected = true,
    showRating = true,
    location = "onboarding",
} = defineProps<{
    embedded?: boolean;
    readings: ReadingsState | null;
    /** False while readings are still landing one at a time. */
    settled: boolean;
    /** Empty of anything but `private` when the org has publishing turned off. */
    availableAccessLevels: AccessLevel[];
    /** False means readings were generated from assessment answers without calendar context. */
    calendarConnected?: boolean;
    /** Offer the rating bar on each reading. Off for the fixture-backed staff gallery. */
    showRating?: boolean;
    /** Which setup entry point is rendering the step. */
    location?: LabInsightLocation;
}>();

const emit = defineEmits<{ continue: [] }>();

const { t } = useI18n();

/* An org with publishing off must not be promised a "who sees it" choice the
   publish screen will not offer. */
const canPublish = computed(() => availableAccessLevels.some((level) => level !== "private"));

/* Written, not promised: generation starts when the assessment completes, so by
   the time this screen is on show the profile usually already exists. */
const handoffCopy = computed(() =>
    canPublish.value ? t("onboarding.calendarFirst.sharpenedReadingsStep.handoff.publishable") : t("onboarding.calendarFirst.sharpenedReadingsStep.handoff.private"),
);

/* Hide this lede when reading generation fails; it promises insights the screen cannot provide. */
const lede = computed(() => t("onboarding.calendarFirst.sharpenedReadingsStep.lede"));

const READING_SLOT_COUNT = 3;

/*
 * Unlike the first pass, a sparse calendar is not disqualifying here: these
 * readings are driven by the answers the user just gave, which is the whole
 * claim of the screen. A run that failed part-way keeps whatever landed before
 * it broke: those readings are real and already written against the answers, and
 * discarding them for an apology would lose the user something we have.
 */
const cards = computed(() => readings?.readings ?? []);

const hasReadings = computed(() => cards.value.length > 0);

/* Slots still to be filled, held open only while more can still arrive. */
const pendingSlots = computed(() => (settled ? 0 : Math.max(READING_SLOT_COUNT - cards.value.length, 0)));

/* Nothing arrived and nothing more is coming — the only state the apology fits. */
const showFailure = computed(() => settled && !hasReadings.value);

/*
 * "It broke" and "it is still running and you have waited long enough" are
 * different things to be told, and only the first is worth an apology. The page
 * lands here on either, so the distinction has to be drawn from the status: a
 * terminal `failed` is a real error, anything still mid-flight is the deadline.
 */
type Failure = "failed" | "timed_out" | "empty";

const failure = computed<Failure>(() => {
    if (readings === null) {
        return "timed_out";
    }
    if (readings.status === "failed") {
        return "failed";
    }
    if (readings.status === "not_started" || readings.status === "running") {
        return "timed_out";
    }
    return "empty";
});

const eyebrowCopy = computed(() =>
    failure.value === "failed" ? t("onboarding.calendarFirst.sharpenedReadingsStep.eyebrow.failed") : t("onboarding.calendarFirst.sharpenedReadingsStep.eyebrow.missing"),
);

const headlineCopy = computed(() => {
    if (showFailure.value) {
        return t("onboarding.calendarFirst.sharpenedReadingsStep.headline.failure");
    }
    return calendarConnected ? t("onboarding.calendarFirst.sharpenedReadingsStep.headline.calendar") : t("onboarding.calendarFirst.sharpenedReadingsStep.headline.noCalendar");
});

const noticeHeadline = computed(() => {
    if (failure.value === "failed") {
        return t("onboarding.calendarFirst.sharpenedReadingsStep.notice.failed.headline");
    }
    return failure.value === "empty"
        ? t("onboarding.calendarFirst.sharpenedReadingsStep.notice.empty.headline")
        : t("onboarding.calendarFirst.sharpenedReadingsStep.notice.timedOut.headline");
});

const noticeCopy = computed(() => {
    if (failure.value === "failed") {
        return t("onboarding.calendarFirst.sharpenedReadingsStep.notice.failed.copy");
    }
    if (failure.value === "empty") {
        return t("onboarding.calendarFirst.sharpenedReadingsStep.notice.empty.copy");
    }
    return t("onboarding.calendarFirst.sharpenedReadingsStep.notice.timedOut.copy");
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .eyebrow {
        @apply details tracking-wide uppercase inline-flex items-center gap-2 text-ink-muted;
    }

    .readings {
        @apply mt-2 w-full;
    }

    .notice {
        @apply mt-6 w-full items-start px-5 py-4 text-left;
    }

    .notice-body {
        @apply min-w-0;
    }

    .notice-head {
        @apply body-small-bold;
    }

    .notice-copy {
        @apply body-small mt-2;
    }

    .notice-reassurance {
        @apply body-small mt-3 border-t border-notification-warning-border pt-3;
    }

    .handoff {
        @apply mt-6 w-full border-t border-border pt-4;
    }

    .handoff-eyebrow {
        @apply details tracking-wide uppercase text-ink-muted;
    }

    .handoff-title {
        @apply body-semibold mt-1 text-ink;
    }

    .handoff-body {
        @apply body-small mt-2 text-ink italic;
    }
}
</style>
